import { forwardRef, useCallback } from "react";

type Props = {
  icon: React.ReactNode;
  size?: "big" | "medium" | "small" | "x_small";
  callback?: () => void;
  disabled?: boolean;
  background?: React.CSSProperties["background"];
  ["data-cy"]?: string;
};

const RoundIconButton = forwardRef<
  HTMLDivElement,
  Props & React.HTMLAttributes<HTMLDivElement>
>(
  (
    {
      icon,
      size,
      callback,
      disabled,
      background,
      "data-cy": dataCy,
      onClick,
      onKeyDown,
      ...rest
    },
    ref
  ) => {
    const getButtonSize = useCallback(() => {
      const options: Record<string, string> = {
        big: "h-8 w-8",
        medium: "h-6 w-6",
        small: "h-5 w-5",
        x_small: "h-4 w-4",
      };

      return size ? options[size] : options.big;
    }, [size]);

    return (
      <div
        ref={ref}
        {...rest}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        className={`flex ${getButtonSize()} transition-transform items-center justify-center rounded-full text-[16px] outline-none duration-150 ease-out-soft mobile:me-0
        ${
          disabled
            ? "bg-[#D1D1D1] text-white"
            : `${
                background || "bg-background"
              } cursor-pointer text-secondary hover:brightness-105 active:scale-90
            `
        }`}
        onClick={(e) => {
          if (!disabled) callback?.();
          onClick?.(e);
        }}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            callback?.();
          }
          onKeyDown?.(e);
        }}
        data-cy={dataCy}
      >
        {icon}
      </div>
    );
  }
);

RoundIconButton.displayName = "RoundIconButton";

export default RoundIconButton;
